import { useCallback, useEffect, useState } from 'react';
import { fetchPlanContext } from '../lib/billingApi';

/**
 * Runtime plan limits, usage, and feature flags for the current organization.
 */
export function usePlanContext(organizationId) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(Boolean(organizationId));
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setContext(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPlanContext(organizationId);
      setContext(data);
      return data;
    } catch (err) {
      setError(err);
      setContext(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canUseFeature = useCallback(
    (key) => Boolean(context?.features?.[key]),
    [context]
  );

  const usagePercent = useCallback(
    (resource) => {
      const u = context?.usage?.[`${resource}_count`] ?? context?.usage?.[resource];
      const lim = context?.limits?.[`max_${resource}`] ?? context?.limits?.[`${resource}_limit`];
      if (lim == null || lim < 0) return 0;
      return Math.min(100, ((u || 0) / Math.max(1, lim)) * 100);
    },
    [context]
  );

  return {
    context,
    loading,
    error,
    refresh,
    canUseFeature,
    usagePercent,
    isTrialing: context?.status === 'trialing',
    isPastDue: context?.status === 'past_due',
    scheduledDowngrade: context?.pending_plan_id || context?.scheduled_change_at,
  };
}
